import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  ArrowLeft,
  Sparkles,
  Calendar,
  CreditCard,
  AlertCircle,
  CheckCircle2,
  Loader2,
  ArrowUpRight,
  Clock,
  Shield,
  XCircle,
  RotateCcw
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const PLAN_FEATURES = {
  free: [
    "Up to 5 client profiles",
    "Basic service menu (max 5)",
    "Preference sharing links",
    "Community support"
  ],
  trial: [
    "Unlimited client profiles",
    "Full service menu with upsells",
    "Custom intake flow",
    "Analytics dashboard",
    "Payment links",
    "Client kanban pipeline"
  ],
  solo: [
    "Unlimited client profiles",
    "Full service menu with upsells",
    "Custom intake flow (4 steps)",
    "Inspiration gallery + looks",
    "Analytics dashboard",
    "Payment links (Venmo/CashApp/Zelle)",
    "Inner circle management",
    "Client kanban pipeline",
    "Acuity + Tap In integrations"
  ],
  team: [
    "Everything in Solo",
    "Up to 5 team members",
    "Shared client directory",
    "Team scheduling",
    "Role-based permissions",
    "Team performance analytics"
  ],
};

const SubscriptionPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  useEffect(() => {
    fetchSubscription();
  }, []);

  const fetchSubscription = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/subscription/current`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSubscription(data.subscription);
      }
    } catch (error) {
      console.error("Error fetching subscription:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    setShowCancelDialog(false);
    setActionLoading("cancel");
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/subscription/cancel`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchSubscription();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Could not cancel subscription");
      }
    } catch {
      toast.error("Connection error");
    }
    setActionLoading(null);
  };

  const handleReactivate = async () => {
    setActionLoading("reactivate");
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/subscription/reactivate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.needs_checkout) {
          navigate("/pricing");
        } else {
          toast.success(data.message);
          fetchSubscription();
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || "Could not reactivate");
      }
    } catch {
      toast.error("Connection error");
    }
    setActionLoading(null);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric", month: "long", day: "numeric"
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-300" />
      </div>
    );
  }

  const plan = subscription?.plan || "free";
  const status = subscription?.status || "none";
  const isCancelling = subscription?.cancel_at_period_end;
  const isTrialing = status === "active" && plan === "trial";
  const features = PLAN_FEATURES[plan] || PLAN_FEATURES.free;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="subscription-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <Link to="/me" className="text-gray-400 hover:text-black transition-colors" data-testid="sub-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="text-lg font-semibold">Subscription</h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8">
        {!subscription ? (
          /* No Subscription */
          <Card>
            <CardContent className="py-16 text-center">
              <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-6">
                <Sparkles className="w-8 h-8 text-gray-400" />
              </div>
              <h2 className="text-xl font-bold mb-2">No Active Subscription</h2>
              <p className="text-gray-500 text-sm mb-8 max-w-sm mx-auto">
                Start your free trial or choose a plan to unlock all features for your business.
              </p>
              <Button
                onClick={() => navigate("/pricing")}
                className="bg-gray-900 hover:bg-black text-white rounded-full px-8 h-11"
                data-testid="view-plans-btn"
              >
                View Plans
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Current Plan Card */}
            <Card className="overflow-hidden">
              <div className={`h-1.5 ${
                plan === "solo" ? "bg-violet-500" :
                plan === "team" ? "bg-blue-500" :
                plan === "trial" ? "bg-emerald-500" :
                "bg-gray-300"
              }`} />
              <CardContent className="pt-6 pb-6">
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                      plan === "solo" ? "bg-violet-100" :
                      plan === "team" ? "bg-blue-100" :
                      plan === "trial" ? "bg-emerald-100" :
                      "bg-gray-100"
                    }`}>
                      {plan === "solo" ? <Sparkles className="w-7 h-7 text-violet-600" /> :
                       plan === "trial" ? <Clock className="w-7 h-7 text-emerald-600" /> :
                       <CreditCard className="w-7 h-7 text-gray-500" />}
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">
                        {plan === "trial" ? "Free Trial (Solo)" :
                         plan === "solo" ? "Solo" :
                         plan === "team" ? "Team" :
                         plan.charAt(0).toUpperCase() + plan.slice(1)}
                      </h2>
                      <p className="text-gray-500 text-sm">
                        {subscription.billing_cycle === "annual" ? "Annual" : "Monthly"} billing
                      </p>
                    </div>
                  </div>
                  <Badge
                    className={`${
                      isCancelling ? "bg-amber-100 text-amber-700 border-amber-200" :
                      status === "active" ? "bg-emerald-100 text-emerald-700 border-emerald-200" :
                      status === "expired" ? "bg-red-100 text-red-700 border-red-200" :
                      "bg-gray-100 text-gray-600 border-gray-200"
                    } border`}
                    data-testid="sub-page-status"
                  >
                    {isCancelling ? "Cancelling" : status}
                  </Badge>
                </div>

                {/* Billing Details Grid */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                    <Calendar className="w-4 h-4 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-500">Current Period</p>
                      <p className="text-sm font-medium text-gray-800">
                        {formatDate(subscription.current_period_start)} - {formatDate(subscription.current_period_end)}
                      </p>
                    </div>
                  </div>

                  {isTrialing && subscription.trial_ends_at && (
                    <div className="flex items-start gap-3 p-3 bg-emerald-50 rounded-xl">
                      <Clock className="w-4 h-4 text-emerald-500 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-500">Trial Ends</p>
                        <p className="text-sm font-medium text-emerald-700">
                          {formatDate(subscription.trial_ends_at)}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Cancellation Notice */}
                {isCancelling && (
                  <div className="flex items-start gap-3 mt-5 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                    <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <p className="font-medium text-amber-800 text-sm">Subscription cancelling</p>
                      <p className="text-xs text-amber-600 mt-1">
                        Full access until {formatDate(subscription.current_period_end)}.
                        Your plan will not renew after that date.
                      </p>
                    </div>
                    <Button
                      size="sm"
                      onClick={handleReactivate}
                      disabled={actionLoading === "reactivate"}
                      className="bg-amber-600 hover:bg-amber-700 text-white rounded-lg shrink-0"
                      data-testid="reactivate-btn"
                    >
                      {actionLoading === "reactivate" ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5 mr-1" />}
                      Reactivate
                    </Button>
                  </div>
                )}

                {/* Expired Notice */}
                {status === "expired" && (
                  <div className="flex items-start gap-3 mt-5 p-4 bg-red-50 border border-red-200 rounded-xl">
                    <XCircle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <p className="font-medium text-red-800 text-sm">Subscription expired</p>
                      <p className="text-xs text-red-600 mt-1">
                        Your data is preserved. Resubscribe to restore full access.
                      </p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => navigate("/pricing")}
                      className="bg-red-600 hover:bg-red-700 text-white rounded-lg shrink-0"
                    >
                      Resubscribe
                    </Button>
                  </div>
                )}

                {/* Actions Row */}
                <div className="flex flex-wrap gap-3 mt-6 pt-5 border-t border-gray-100">
                  {status === "active" && !isCancelling && plan !== "trial" && (
                    <Button
                      variant="outline"
                      onClick={() => setShowCancelDialog(true)}
                      disabled={actionLoading === "cancel"}
                      className="rounded-lg text-gray-500"
                      data-testid="cancel-subscription-btn"
                    >
                      {actionLoading === "cancel" && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                      Cancel Subscription
                    </Button>
                  )}

                  {plan === "trial" && (
                    <Button
                      onClick={() => navigate("/pricing")}
                      className="bg-gray-900 hover:bg-black text-white rounded-lg"
                    >
                      Upgrade Now
                    </Button>
                  )}

                  {plan !== "trial" && plan !== "team" && status === "active" && (
                    <Button
                      onClick={() => navigate("/pricing")}
                      className="bg-gray-900 hover:bg-black text-white rounded-lg"
                    >
                      Upgrade Plan
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Features Included */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold">Features Included</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 gap-2">
                  {features.map((feature, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Quick Links */}
            <Card>
              <CardContent className="py-4">
                <div className="space-y-1">
                  <Link
                    to="/pricing"
                    className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <span className="text-sm font-medium text-gray-700">Compare Plans</span>
                    <ArrowUpRight className="w-4 h-4 text-gray-400" />
                  </Link>
                  <Link
                    to="/me"
                    className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <span className="text-sm font-medium text-gray-700">Back to Dashboard</span>
                    <ArrowUpRight className="w-4 h-4 text-gray-400" />
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              Your subscription will not renew at the end of the current billing period.
              You will keep full access to all features until then. No refund will be issued.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Plan</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancel}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Yes, Cancel
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SubscriptionPage;
